package ai.wagemix.data

import ai.wagemix.net.models.WMAccountRules
import ai.wagemix.net.models.WMAuthFailure
import com.fsryan.foundation.net.client.FSAPIException
import com.fsryan.foundation.net.client.FSResponse

/*
 * The account routes of the Wagemix API (accounts-and-email plan §2.2) answer each failure the auth screens tell
 * apart with its own status, and a 400 names the field in `param` and the rule in `value`. These map those answers
 * onto the [WMAuthFailure] reasons the screens already render.
 */

/** Throws the [WMAuthException] an account route's failure means, unless the status is 2xx. */
fun FSResponse<*, *>.throwAccountFailureUnlessSuccess() {
    if (status in 200..299) {
        return
    }
    throw WMAuthException(reason = accountFailureReason(), message = exception?.message)
}

/** The body of an account route's success, or the [WMAuthException] its failure means. */
fun <D: Any> FSResponse<D, *>.accountDataOrThrow(): D {
    throwAccountFailureUnlessSuccess()
    return data ?: throw WMAuthException(reason = WMAuthFailure.Unknown, message = "No body in the response")
}

internal fun FSResponse<*, *>.accountFailureReason(): WMAuthFailure {
    val error = (exception as? FSAPIException)?.errorsResponse
    return when {
        exception != null && exception !is FSAPIException -> WMAuthFailure.Network
        status == 409 -> WMAuthFailure.UserAlreadyExists
        status == 429 -> WMAuthFailure.TooManyAttempts
        status == 404 -> WMAuthFailure.AccountNotFound
        status != 400 -> WMAuthFailure.Unknown
        error?.param == "token" -> when (error.value) {
            WMAccountRules.ERROR_EXPIRED -> WMAuthFailure.ExpiredLink
            else -> WMAuthFailure.InvalidLink
        }
        error?.value == WMAccountRules.ERROR_WEAK -> WMAuthFailure.WeakPassword
        error?.param == "email" -> WMAuthFailure.InvalidEmail
        else -> WMAuthFailure.Unknown
    }
}
